import React, { useState, useEffect, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useEditMode } from '../../contexts/EditModeContext';
import { industryList } from '../../constants/industryList';
import { 
  EMPTY_FORM, 
  INFORMATION_CHANNELS, 
  ENTITY_STATUS_OPTIONS, 
  FILE_SELECT_TEXT,
  NOTIFICATION_MESSAGES 
} from '../../constants/formConstants';
import { loadConfigFromS3, getCurrentConfig } from '../../constants/configLoader';
import config from '../../constants/config.json';
import { apiClient } from '../../services/authService';
import { allowedAttachmentsFileTypes } from '../../constants/attachments-allow';
import ApplyTitleHeader from '../common/ApplyTitleHeader';
import ApplyAuth from '../auth/ApplyAuth';
import ConsentRequiredModal from '../modals/ConsentRequiredModal';
import OverwriteConfirmModal from '../modals/OverwriteConfirmModal';
import ValidationToastModal from '../modals/ValidationToastModal';
import { IoCalendarOutline } from 'react-icons/io5';
import { MdOutlineFileDownload } from 'react-icons/md';
import { IoMdRadioButtonOff, IoMdRadioButtonOn } from 'react-icons/io';
import { FaTrashAlt } from 'react-icons/fa';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import DatePicker from 'react-datepicker';
import Form from 'react-bootstrap/Form';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/forms/applicationForm.css';
import { sectorImages } from '../../constants/images';
import UploadProgressModal from '../modals/UploadProgressModal';
import SaveConfirmModal from '../modals/SaveConfirmModal';
import '../../styles/common/modal.css';

const ApplicationForm = ({ 
  mode = 'create',  // 'create' | 'edit'
  hasApplication = false,
  hasDraft = false,
  initialData = EMPTY_FORM,
  onSave,
  onCancel,
  isLoggedIn,
  userEmail,
  onLogout
}) => {
  const [formData, setFormData] = useState(initialData || EMPTY_FORM);
  const [industryDropdownOpen, setIndustryDropdownOpen] = useState(false);
  const [entityDropdownOpen, setEntityDropdownOpen] = useState(false);
  const [genderDropdownOpen, setGenderDropdownOpen] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showUploadProgressModal, setShowUploadProgressModal] = useState(false);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [showValidationToast, setShowValidationToast] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [invalidElement, setInvalidElement] = useState(null);
  const [currentFileType, setCurrentFileType] = useState('');
  const [pendingFileInput, setPendingFileInput] = useState(null);
  const [filesProgress, setFilesProgress] = useState([]);
  const [totalProgress, setTotalProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [announcementUrl, setAnnouncementUrl] = useState(config.CONFIG_S3_ANNOUNCEMENT || '');
  const [showZeroSizeModal, setShowZeroSizeModal] = useState(false);
  const [zeroSizeFileName, setZeroSizeFileName] = useState('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState('');
  const [isFileSelectLoading, setIsFileSelectLoading] = useState({
    applicationForm: false,
    irPitchDeck: false,
    additionalDocuments: false
  });
  const fileSelectTimeoutRefs = useRef({
    applicationForm: null,
    irPitchDeck: null,
    additionalDocuments: null
  });
  const industryDropdownRef = useRef(null);
  const entityDropdownRef = useRef(null);
  const genderDropdownRef = useRef(null);
  const applicationFormInputRef = useRef(null);
  const irPitchDeckInputRef = useRef(null);
  const additionalDocumentsInputRef = useRef(null);
  const navigate = useNavigate();
  const { isEditMode, setIsEditMode } = useEditMode();
  const formRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState({
    applicationForm: null,
    irPitchDeck: null,
    additionalDocuments: null
  });
  const [fileSizes, setFileSizes] = useState({
    applicationForm: null,
    irPitchDeck: null,
    additionalDocuments: null
  });
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState(null);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showParticipantInfoModal, setShowParticipantInfoModal] = useState(false);
  const [modalActionType, setModalActionType] = useState('navigate'); // 'navigate', 'draft', 'submit'
  
  // 허용된 파일 확장자를 accept 속성 포맷으로 변환
  const acceptedFileTypes = Object.keys(allowedAttachmentsFileTypes).map(ext => `.${ext}`).join(',');
  
  // 컴포넌트 마운트 시 실제 mode 값을 콘솔에 출력
  useEffect(() => {
    console.log('ApplicationForm 마운트: ', {
      mode,
      hasApplication,
      hasDraft,
      isEditMode,
      '실제 동작 모드': isEditMode ? 'edit' : (hasApplication ? 'edit' : (hasDraft ? 'edit' : 'create'))
    });
    
    // 초기 데이터에서 파일 정보 확인 및 설정
    if (initialData) {
      console.log('초기 데이터 파일 정보 확인:', {
        applicationForm: initialData.applicationForm,
        irPitchDeck: initialData.irPitchDeck,
        additionalDocuments: initialData.additionalDocuments
      });
      
      // applicationForm 필드에 URL이 있으면 파일이 업로드된 것으로 간주
      if (initialData.applicationForm) {
        // 파일 확장자 추출 (URL에서 확장자 추출 시도)
        const applicationFormExt = initialData.applicationForm.split('.').pop().split('?')[0].toLowerCase();
        
        // 파일명 형식 설정 (확장자와 함께)
        const applicationFormFileName = `applicationForm.${applicationFormExt}`;
        
        // 사용자에게 보여줄 파일명 설정
        setSelectedFiles(prev => ({
          ...prev,
          applicationForm: applicationFormFileName
        }));
        
        // 파일 크기 정보 (기존 파일은 크기를 알 수 없으므로 '업로드됨'으로 표시)
        setFileSizes(prev => ({
          ...prev,
          applicationForm: `uploaded${hasDraft ? ' as a draft' : hasApplication ? ' has been submitted' : ''}`,
        }));
      }
      
      // irPitchDeck 필드에 URL이나 문자열이 있으면 파일이 업로드된 것으로 간주
      if (initialData.irPitchDeck) {
        // 파일 확장자 추출 (문자열에서 확장자 추출 시도)
        const irPitchDeckExt = initialData.irPitchDeck.split('.').pop().split('?')[0].toLowerCase();
        
        // 파일명 형식 설정 (확장자와 함께)
        const irPitchDeckFileName = `irPitchDeck.${irPitchDeckExt}`;
        
        // 사용자에게 보여줄 파일명 설정
        setSelectedFiles(prev => ({
          ...prev,
          irPitchDeck: irPitchDeckFileName
        }));
        
        // 파일 크기 정보 (기존 파일은 크기를 알 수 없으므로 '업로드됨'으로 표시)
        setFileSizes(prev => ({
          ...prev,
          irPitchDeck: `uploaded${hasDraft ? ' as a draft' : hasApplication ? ' has been submitted' : ''}`
        }));
      }
      
      // additionalDocuments 필드에 URL이나 문자열이 있으면 파일이 업로드된 것으로 간주
      if (initialData.additionalDocuments) {
        // 파일 확장자 추출 (문자열에서 확장자 추출 시도)
        const additionalDocumentsExt = initialData.additionalDocuments.split('.').pop().split('?')[0].toLowerCase();
        
        // 파일명 형식 설정 (확장자와 함께)
        const additionalDocumentsFileName = `additionalDocuments.${additionalDocumentsExt}`;
        
        // 사용자에게 보여줄 파일명 설정
        setSelectedFiles(prev => ({
          ...prev,
          additionalDocuments: additionalDocumentsFileName
        }));
        
        // 파일 크기 정보 (기존 파일은 크기를 알 수 없으므로 '업로드됨'으로 표시)
        setFileSizes(prev => ({
          ...prev,
          additionalDocuments: `uploaded${hasDraft ? ' as a draft' : hasApplication ? ' has been submitted' : ''}`
        }));
      }
    }
  }, [mode, hasApplication, hasDraft, isEditMode, initialData]);

  // S3에서 CONFIG_S3_ANNOUNCEMENT 설정값 로드
  useEffect(() => {
    // 로컬 설정에서 기본값으로 초기화
    setAnnouncementUrl(config.CONFIG_S3_ANNOUNCEMENT || '');
    
    // S3에서 설정 로드 시도
    const CONFIG_S3_URL = import.meta.env.CONFIG_S3_URL;
    if (CONFIG_S3_URL) {
      // 강제 로드 옵션 사용하지 않음 - 캐싱 메커니즘 활용
      loadConfigFromS3(CONFIG_S3_URL)
        .then(loadedConfig => {
          if (loadedConfig.CONFIG_S3_ANNOUNCEMENT) {
            setAnnouncementUrl(loadedConfig.CONFIG_S3_ANNOUNCEMENT);
          }
        })
        .catch(error => {
          console.error('S3 설정 로드 실패:', error);
        });
    }
  }, []);

  useEffect(() => {
    setIsEditMode(true);

    // Simple beforeunload event handler - show browser default warning only
    const handleBeforeUnload = (e) => {
      if (isEditMode) {
        // Standard approach to show browser's default confirmation dialog
        e.preventDefault();
        // For IE and legacy browser compatibility
        e.returnValue = 'Your changes may not be saved. Are you sure you want to leave this page?';
        return 'Your changes may not be saved. Are you sure you want to leave this page?';
      }
    };

    // Detect and handle browser back button
    const handlePopState = (e) => {
      if (isEditMode) {
        const confirmLeave = window.confirm('Your changes may not be saved. Are you sure you want to leave this page?');
        if (confirmLeave) {
          // User confirmed - allow navigation to previous page
          setIsEditMode(false); // Disable edit mode before page navigation
          navigate(-1);
        } else {
          // User canceled - stay on current page
          // Since popstate event already fired, add current URL back to history
          window.history.pushState(null, '', window.location.pathname);
        }
      }
    };

    // Register event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Set initial history state
    window.history.pushState(null, '', window.location.pathname);
    
    // Cleanup on component unmount
    return () => {
      setIsEditMode(false);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [setIsEditMode, navigate, isEditMode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    console.log('hasApplication on ApplicationForm', hasApplication);
    console.log('mode on ApplicationForm', mode);
    const handleClickOutside = (event) => {
      if (industryDropdownRef.current && !industryDropdownRef.current.contains(event.target)) {
        setIndustryDropdownOpen(false);
      }
      if (entityDropdownRef.current && !entityDropdownRef.current.contains(event.target)) {
        setEntityDropdownOpen(false);
      }
      if (genderDropdownRef.current && !genderDropdownRef.current.contains(event.target)) {
        setGenderDropdownOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // URL 필드들의 경우 URL 형식 검증 및 수정
    // if ((name === 'website' || name === 'socialMedia' || name === 'promotionalVideo') && 
    //     value && !value.match(/^https?:\/\//)) {
    //   // http:// 또는 https://가 없는 경우 http://를 자동으로 추가
    //   setFormData(prev => ({
    //     ...prev,
    //     [name]: `http://${value}`
    //   }));
    // } else {
    //   // 일반적인 필드 처리
    //   setFormData(prev => ({
    //     ...prev,
    //     [name]: value
    //   }));
    // }
    
    // 폼 수정 모드 활성화 - hasApplication 또는 hasDraft가 true인 경우에만
    if (hasApplication || hasDraft) {
      setIsEditMode(true);
    }
  };

  // 파일 필드명 변환 공통 함수
  const getFormattedFileNames = (selectedFiles) => {
    const result = {};
    
    if (selectedFiles.applicationForm) {
      result.applicationForm = 'applicationForm.' + selectedFiles.applicationForm.split('.').pop().toLowerCase();
    } else if (hasDraft && selectedFiles.applicationForm === null) {
      // hasDraft가 true이고 파일이 명시적으로 null로 설정된 경우 (삭제됨)
      result.applicationForm = '';
    }
    
    if (selectedFiles.irPitchDeck) {
      result.irPitchDeck = 'irPitchDeck.' + selectedFiles.irPitchDeck.split('.').pop().toLowerCase();
    } else if (hasDraft && selectedFiles.irPitchDeck === null) {
      // hasDraft가 true이고 파일이 명시적으로 null로 설정된 경우 (삭제됨)
      result.irPitchDeck = '';
    }
    
    if (selectedFiles.additionalDocuments) {
      result.additionalDocuments = 'additionalDocuments.' + selectedFiles.additionalDocuments.split('.').pop().toLowerCase();
    } else if (hasDraft && selectedFiles.additionalDocuments === null) {
      // hasDraft가 true이고 파일이 명시적으로 null로 설정된 경우 (삭제됨)
      result.additionalDocuments = '';
    }
    
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('[디버깅] handleSubmit 호출됨');
    
    // Check for privacy agreement first
    if (!formData.privacyAgreement && mode === 'create') {
      setShowConsentModal(true);
      return;
    }
    
    // 추가 필수 항목 검사 (industry, applicationForm, irPitchDeck)
    const customValidation = () => {
      console.log('[디버깅] 추가 필수 항목 검사 시작');
      
      // 필수 필드 및 해당 레이블 정의
      const requiredFields = [
        { id: 'teamName', label: 'Please enter the Team(Company) Name', element: document.getElementById('teamName') },
        { id: 'entityStatus', label: 'Please select the Entity Status', element: document.querySelector('.dropdown-button') },
        { id: 'location', label: 'Please enter the Headquarters location', element: document.getElementById('location') },
        { id: 'ceo', label: 'Please enter the CEO', element: document.getElementById('ceo') },
        { id: 'gender', label: 'Please select the Gender', element: document.getElementById('gender') },
        { id: 'participant', label: 'Please enter the Participant', element: document.getElementById('participant') },
        { id: 'participantTitle', label: 'Please enter the Participant\'s title', element: document.getElementById('participantTitle') },
        { id: 'participantNationality', label: 'Please enter the Participant\'s nationality', element: document.getElementById('participantNationality') },
        { id: 'businessItem', label: 'Please enter the Business Item', element: document.getElementById('businessItem') },
        { id: 'informationChannel', label: 'Please select the Source of information', element: document.querySelector('input[name="informationChannel"]') },
      ];
      
      // 법인 상태가 "Pre-established"가 아닌 경우에만 설립 날짜 검증
      if (formData.entityStatus !== 'Pre-established') {
        requiredFields.push({ 
          id: 'establishedDate', 
          label: 'Please select the Established date', 
          element: document.getElementById('establishedDate') 
        });
      }
      
      // Industry 확인
      if (!formData.industry) {
        console.log('[디버깅] Industry 필드 누락');
        // industry dropdown 버튼 참조
        const industryDropdownBtn = document.querySelector('.dropdown-button');
        showToast('Please select the Industry', industryDropdownBtn);
        return false;
      }
      
      // 필수 필드 검증
      for (const field of requiredFields) {
        // informationChannel은 특별 처리 (라디오 버튼)
        if (field.id === 'informationChannel') {
          if (!formData.informationChannel) {
            console.log(`[디버깅] ${field.id} 필드 누락`);
            // 첫 번째 라디오 버튼 요소 찾기
            const radioElement = document.querySelector('input[name="informationChannel"]');
            showToast(field.label, radioElement);
            return false;
          }
          continue;
        }
        
        if (!formData[field.id] || formData[field.id].trim() === '') {
          console.log(`[디버깅] ${field.id} 필드 누락`);
          showToast(field.label, field.element);
          return false;
        }
      }
      
      // Application Form 파일 확인 (hasApplication이 true가 아닌 경우)
      const applicationFormInput = document.getElementById('applicationForm');
      if (!hasApplication && (!applicationFormInput.files || applicationFormInput.files.length === 0) && !formData.applicationForm) {
        console.log('[디버깅] Application Form 파일 누락');
        showToast('Please upload the Application Form file', applicationFormInput);
        return false;
      }
      
      // IR Pitch Deck 파일 확인 (hasDraft가 true가 아닌 경우)
      const irPitchDeckInput = document.getElementById('irPitchDeck');
      if (!hasDraft && (!irPitchDeckInput.files || irPitchDeckInput.files.length === 0) && !formData.irPitchDeck) {
        console.log('[디버깅] IR Pitch Deck 파일 누락');
        showToast('Please upload the IR Pitch Deck file', irPitchDeckInput);
        return false;
      }
      
      console.log('[디버깅] 추가 필수 항목 검사 통과');
      return true;
    };
    
    // Use HTML5 form validation
    const form = formRef.current;
    if (form) {
      // Find the first empty required field
      const firstInvalidField = form.querySelector(':invalid');
      
      console.log('[디버깅] 유효하지 않은 필드 확인:', firstInvalidField);
      
      if (firstInvalidField) {
        // Focus on the first invalid field
        firstInvalidField.focus();
        
        // Set validation message based on field name or type
        let fieldName = firstInvalidField.name || firstInvalidField.id;
        
        // 필드 이름을 사용자 친화적으로 변환
        let friendlyFieldName = fieldName.replace(/([A-Z])/g, ' $1').toLowerCase();
        friendlyFieldName = friendlyFieldName.charAt(0).toUpperCase() + friendlyFieldName.slice(1);
        
        // 영어 메시지 설정
        const message = `Please check the ${friendlyFieldName} field`;
        console.log('[디버깅] 유효성 검사 메시지:', message);
        console.log('[디버깅] 대상 요소:', firstInvalidField);
        
        // 새로운 showToast 함수 사용
        showToast(message, firstInvalidField);
        
        return;
      }
      
      // HTML5 검증이 통과하면 추가 필수 항목 검사
      if (!customValidation()) {
        return;
      }
    }
    
    try {
      let apiResponse;
      
      // 선택된 파일명 추가 (확장자 포함)
      const formDataWithFileNames = {
        ...formData,
        ...getFormattedFileNames(selectedFiles)
      };
      
      // 수정 시 삭제된 파일 정보 처리
      if (mode === 'edit' && hasDraft) {
        if (selectedFiles.applicationForm === null && !formDataWithFileNames.hasOwnProperty('applicationForm')) {
          console.log('[제출] applicationForm 필드가 삭제됨, 빈 문자열로 설정');
          formDataWithFileNames.applicationForm = '';
        }
        
        if (selectedFiles.irPitchDeck === null && !formDataWithFileNames.hasOwnProperty('irPitchDeck')) {
          console.log('[제출] irPitchDeck 필드가 삭제됨, 빈 문자열로 설정');
          formDataWithFileNames.irPitchDeck = '';
        }
        
        if (selectedFiles.additionalDocuments === null && !formDataWithFileNames.hasOwnProperty('additionalDocuments')) {
          console.log('[제출] additionalDocuments 필드가 삭제됨, 빈 문자열로 설정');
          formDataWithFileNames.additionalDocuments = '';
        }
      }
      console.log('[제출] -- 제출 처리 시작', {
        mode,
        hasApplication,
        hasDraft
      });
      // 폼 제출 처리
      if (mode === 'create' || (mode === 'edit' && hasDraft)) {
        // 신규 생성 API 호출
        apiResponse = await apiClient.post('/user/apply/create', {
          email: userEmail,
          ...formDataWithFileNames
        });
      } else if (mode === 'edit' && hasApplication) {
        // 기존 신청서 수정 API 호출
        apiResponse = await apiClient.post('/user/apply/update', {
          email: userEmail,
          ...formDataWithFileNames
        });
      } else {
        // API 연결 없는 기존 로직
        onSave(formDataWithFileNames);
        setIsEditMode(false);
        // 성공 모달 표시
        setModalActionType('submit');
        setShowSaveConfirmModal(true);
        return;
      }
      
      // API 응답 확인
      if (apiResponse && apiResponse.data && apiResponse.data.success) {
        // 파일 업로드 처리
        const applicationFormInput = document.getElementById('applicationForm');
        const irPitchDeckInput = document.getElementById('irPitchDeck');
        const additionalDocumentsInput = document.getElementById('additionalDocuments');
        
        // 업로드할 파일 목록 생성
        const filesToUpload = [];
        
        if (applicationFormInput && applicationFormInput.files.length > 0) {
          filesToUpload.push({
            id: 'applicationForm',
            inputElement: applicationFormInput,
            file: applicationFormInput.files[0],
            name: 'Application Form',
            progress: 0,
            uploaded: false
          });
        }
        
        if (irPitchDeckInput && irPitchDeckInput.files.length > 0) {
          filesToUpload.push({
            id: 'irPitchDeck',
            inputElement: irPitchDeckInput,
            file: irPitchDeckInput.files[0],
            name: 'IR Pitch Deck',
            progress: 0,
            uploaded: false
          });
        }
        
        if (additionalDocumentsInput && additionalDocumentsInput.files.length > 0) {
          filesToUpload.push({
            id: 'additionalDocuments',
            inputElement: additionalDocumentsInput,
            file: additionalDocumentsInput.files[0],
            name: 'Additional Documents',
            progress: 0,
            uploaded: false
          });
        }
        
        // 업로드할 파일이 있는 경우에만 진행
        if (filesToUpload.length > 0) {
          setFilesProgress(filesToUpload.map(file => ({ name: file.name, progress: 0 })));
          setTotalProgress(0);
          setShowUploadProgressModal(true);
          setIsUploading(true);
          
          try {
            // 파일들의 정보 준비
            const fileData = {};
            const fileObjects = {};
            
            // 각 파일 정보 수집
            filesToUpload.forEach(fileInfo => {
              const file = fileInfo.file;
              const fileExtension = file.name.split('.').pop().toLowerCase();
              fileData[fileInfo.id] = `${fileInfo.id}.${fileExtension}`;
              fileObjects[fileInfo.id] = {
                file: file,
                extension: fileExtension,
                contentType: allowedAttachmentsFileTypes[fileExtension] || 'application/octet-stream',
                name: fileInfo.name
              };
            });
            
            // 단일 API 호출로 모든 파일의 업로드 URL 요청
            console.log('[초안 파일 업로드] 파일 업로드 URL 요청');
            const fileUploadResponse = await apiClient.post('/user/apply/file/upload', {
              email: userEmail,
              ...fileData,
              isDraft: true // 초안 파일임을 표시
            });
            
            console.log('[초안 파일 업로드] 응답:', fileUploadResponse.data);
            
            // 업로드 결과를 저장할 변수
            let uploadResults = [];
            let fileTypes = [];
            
            // 파일 업로드 URL이 있는 경우 파일 업로드 진행
            if (fileUploadResponse && fileUploadResponse.data && fileUploadResponse.data.urls) {
              // 응답에서 각 파일 타입에 대한 URL 추출
              const uploadUrls = fileUploadResponse.data.urls;
              const uploadPromises = [];
              
              // 먼저 fileTypes 초기화 - 업로드 URL에서 타입 추출 (성공 여부와 관계없이)
              fileTypes = Object.keys(uploadUrls);
              console.log('[초안 파일 업로드] 업로드될 파일 타입 목록 (URL 기준):', fileTypes);
              
              // 각 파일 타입에 대해 업로드 진행
              Object.keys(uploadUrls).forEach((fileType, index) => {
                if (uploadUrls[fileType] && fileObjects[fileType]) {
                  console.log(`[초안 파일 업로드] ${fileType} 업로드 준비 중`);
                  // uploadUrls[fileType]는 객체 구조임(url, key, contentType 등 포함)
                  const uploadInfo = uploadUrls[fileType];
                  const uploadUrl = uploadInfo.url; // url 속성 사용
                  const fileObject = fileObjects[fileType];
                  
                  // S3 pre-signed URL의 만료 시간 계산 및 출력
                  if (uploadUrl && uploadUrl.includes('?')) {
                    const urlParams = new URLSearchParams(uploadUrl.split('?')[1]);
                    const expiresParam = urlParams.get('X-Amz-Expires');
                    const dateParam = urlParams.get('X-Amz-Date');
                    
                    if (expiresParam && dateParam) {
                      try {
                        // X-Amz-Date 형식은 보통 YYYYMMDDTHHMMSSZ 형식 (예: 20240506T120000Z)
                        const year = dateParam.substring(0, 4);
                        const month = dateParam.substring(4, 6);
                        const day = dateParam.substring(6, 8);
                        const hour = dateParam.substring(9, 11);
                        const minute = dateParam.substring(11, 13);
                        const second = dateParam.substring(13, 15);
                        
                        // UTC 기준 날짜 생성
                        const baseDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
                        
                        // 만료 시간(초) 추가
                        const expiryDate = new Date(baseDate.getTime() + parseInt(expiresParam) * 1000);
                        
                        // 현재 시간
                        const now = new Date();
                        
                        // 로컬 시간으로 출력
                        console.log(`[초안 S3 URL 만료 정보] ${fileObject.name}`);
                        console.log(`- 현재 시간: ${now.toLocaleString()}`);
                        console.log(`- URL 생성 시간: ${baseDate.toLocaleString()}`);
                        console.log(`- URL 만료 시간: ${expiryDate.toLocaleString()}`);
                        console.log(`- 남은 시간: ${Math.round((expiryDate - now) / 1000)}초`);
                      } catch (error) {
                        console.error('[초안 S3 URL 만료 시간 계산 오류]', error);
                      }
                    }
                  }
                  
                  // 파일 이름 처리 (원본 파일 확장자 유지)
                  const newFileName = `${fileType}.${fileObject.extension}`;
                  console.log(`[초안 파일 업로드] ${fileObject.name} - 변환된 파일명: ${newFileName}`);
                  
                  // 파일 객체 디버깅
                  console.log(`[업로드 디버깅] 파일 객체:`, {
                    originalFile: fileObject.file,
                    name: fileObject.file.name,
                    size: fileObject.file.size,
                    type: fileObject.file.type,
                    lastModified: fileObject.file.lastModified
                  });
                  
                  // 파일 객체 생성 방식 변경 - Blob 사용
                  // const renamedFile = new File([fileObject.file], newFileName, { type: fileObject.contentType });
                  // 파일 객체의 바이너리 데이터 직접 접근
                  const reader = new FileReader();
                  
                  // Promise를 미리 생성하고 배열에 추가
                  const uploadPromise = new Promise((resolve, reject) => {
                    reader.onload = function(e) {
                      const fileContent = e.target.result; // ArrayBuffer 형태의 파일 내용
                      console.log(`[업로드 디버깅] 파일 내용 로드됨: ${fileContent.byteLength} 바이트`);
                      
                      // Blob 객체로 변환하여 업로드
                      const blob = new Blob([fileContent], { type: fileObject.contentType });
                      console.log(`[업로드 디버깅] Blob 생성됨:`, {
                        size: blob.size,
                        type: blob.type
                      });
                      
                      // XMLHttpRequest를 사용하여 업로드
                      const xhr = new XMLHttpRequest();
                      
                      // 업로드 진행 상태 이벤트 리스너
                      xhr.upload.addEventListener('progress', (event) => {
                        if (event.lengthComputable) {
                          const percentComplete = (event.loaded / event.total) * 100;
                          
                          // 특정 파일 진행 상태 업데이트
                          setFilesProgress(prev => {
                            const newProgress = [...prev];
                            const fileIndex = filesToUpload.findIndex(f => f.id === fileType);
                            if (fileIndex !== -1) {
                              newProgress[fileIndex] = { name: fileObject.name, progress: percentComplete };
                            }
                            return newProgress;
                          });
                          
                          // 전체 진행 상태 계산 및 업데이트
                          setTotalProgress(prev => {
                            // 모든 파일의 평균 진행 상태 계산
                            const allProgress = filesProgress.map(f => f.progress);
                            const fileIndex = filesToUpload.findIndex(f => f.id === fileType);
                            if (fileIndex !== -1) {
                              allProgress[fileIndex] = percentComplete;
                            }
                            const avgProgress = allProgress.reduce((sum, curr) => sum + curr, 0) / allProgress.length;
                            return avgProgress;
                          });
                        }
                      });
                      
                      // 업로드 완료 이벤트 리스너
                      xhr.addEventListener('load', () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                          console.log(`[초안 파일 업로드 성공] ${fileObject.name}`);
                          
                          // 디버깅: 응답 상세 정보
                          console.log(`[업로드 디버깅] 응답 상세:`, {
                            status: xhr.status,
                            statusText: xhr.statusText,
                            responseText: xhr.responseText
                          });
                          
                          // 파일 존재 확인 요청 부분 제거 (403 오류 방지)
                          /* 
                          try {
                            const checkFileUrl = uploadUrl.split('?')[0]; // 쿼리 파라미터 제거
                            console.log(`[업로드 확인] 파일 확인 URL: ${checkFileUrl}`);
                            
                            // 파일 확인 요청 (비동기)
                            fetch(checkFileUrl, { method: 'HEAD' })
                              .then(response => {
                                console.log(`[업로드 확인] 결과:`, {
                                  exists: response.ok,
                                  status: response.status,
                                  headers: response.headers ? [...response.headers.entries()] : '헤더 정보 없음'
                                });
                              })
                              .catch(error => {
                                console.error(`[업로드 확인] 오류:`, error);
                              });
                          } catch (checkError) {
                            console.error(`[업로드 확인] 예외:`, checkError);
                          }
                          */
                          
                          // 특정 파일의 진행 상태를 100%로 설정
                          setFilesProgress(prev => {
                            const newProgress = [...prev];
                            const fileIndex = filesToUpload.findIndex(f => f.id === fileType);
                            if (fileIndex !== -1) {
                              newProgress[fileIndex] = { name: fileObject.name, progress: 100 };
                            }
                            return newProgress;
                          });
                          
                          resolve({
                            success: true,
                            fileType: fileType,
                            uploadInfo: uploadInfo
                          });
                        } else {
                          console.error(`[초안 파일 업로드 실패] ${fileObject.name} - 응답 상태: ${xhr.status}, 에러: ${xhr.statusText}`);
                          reject(new Error(xhr.statusText));
                        }
                      });
                      
                      // 에러 이벤트 리스너
                      xhr.addEventListener('error', () => {
                        console.error(`[초안 파일 업로드 오류] ${fileObject.name} - 네트워크 오류 발생`);
                        reject(new Error('Network error'));
                      });
                      
                      console.log(`[초안 파일 업로드] ${fileObject.name} - S3 업로드 시작, URL: ${uploadUrl}`);
                      
                      // 업로드 요청 설정
                      xhr.open('PUT', uploadUrl, true);
                      xhr.setRequestHeader('Content-Type', fileObject.contentType);
                      
                      // CORS 관련 문제 완화 시도
                      xhr.withCredentials = false; // CORS 요청 시 자격 증명 전송 안 함
                      
                      // 디버깅을 위한 요청 세부 정보 출력
                      console.log(`[업로드 디버깅] 요청 정보:`, {
                        method: 'PUT',
                        url: uploadUrl,
                        contentType: fileObject.contentType,
                        fileSize: blob.size,
                        fileName: newFileName,
                        withCredentials: xhr.withCredentials
                      });
                      
                      // 업로드 시작 - File 객체 대신 Blob 사용
                      xhr.send(blob);
                    };
                    
                    reader.onerror = function() {
                      console.error(`[초안 파일 업로드] ${fileObject.name} - 파일 읽기 오류 발생`);
                      reject(new Error('File read error'));
                    };
                    
                    // 파일 읽기 시작
                    reader.readAsArrayBuffer(fileObject.file);
                  });
                  
                  console.log(`[초안 파일 업로드] ${fileType} Promise 배열에 추가됨`);
                  uploadPromises.push(uploadPromise);
                }
              });
              
              // Promise 배열 상태 확인 로그
              console.log('[초안 파일 업로드] Promise 배열 길이:', uploadPromises.length);
              
              // 모든 파일 업로드 완료 대기
              console.log('[초안 파일 업로드] 모든 파일 업로드 Promise 시작');
              uploadResults = await Promise.all(uploadPromises);
              console.log('[초안 파일 업로드] 모든 파일 업로드 완료, 결과:', uploadResults);
              
              // 파일 타입 목록 생성
              if (uploadResults.length > 0) {
                // 복사해야 할 파일 타입 목록
                fileTypes = uploadResults
                  .filter(result => result.success)
                  .map(result => result.fileType);
              }
            } else {
              // URLs가 없는 경우, 업로드할 파일 목록에서 fileTypes 추출
              console.log('[초안 파일 업로드] URLs가 없거나 응답 형식이 다름. 파일 타입만 추출.');
              fileTypes = Object.keys(fileObjects);
              console.log('[초안 파일 업로드] 추출된 파일 타입:', fileTypes);
            }
            
            // 파일 복사 처리 - URLs가 있든 없든 실행
            if (fileTypes.length > 0) {
              // 파일 복사 진행상태 업데이트
              setFilesProgress(prev => {
                return prev.map(file => ({
                  ...file,
                  name: `${file.name} (copying...)`
                }));
              });
              
              // 파일 복사 API 호출을 위한 데이터 준비
              const formattedFileNames = getFormattedFileNames(selectedFiles);
              const copyRequestData = {
                email: userEmail,
                fileTypes: fileTypes,
                isDraft: true,
                ...formattedFileNames
              };
              
              // 요청 데이터 로깅 (디버깅용)
              console.log('[초안 파일 복사] 요청 데이터 상세:', JSON.stringify(copyRequestData, null, 2));
              console.log('[초안 파일 복사] /user/apply/file/copy 호출 시작, 파일 타입:', fileTypes);
              
              try {
                // 복사 API 호출
                const copyResponse = await apiClient.post('/user/apply/file/copy', copyRequestData);
                console.log('[초안 파일 복사] /user/apply/file/copy 응답 성공:', copyResponse.data);
              } catch (copyError) {
                console.error('[초안 파일 복사] /user/apply/file/copy 호출 실패:', copyError);
                // 복사 실패해도 계속 진행
              }
            } else {
              console.log('[초안 파일 복사] 복사할 파일 타입이 없음');
            }
          } catch (error) {
            console.error('[초안 파일 업로드/복사 오류] 처리 중 예외 발생:', error);
          } finally {
            // 업로드 완료 후 상태 업데이트
            setIsUploading(false);
            // 모든 업로드 완료 후 진행 상태 모달을 약간 더 표시한 후 닫기
            setTimeout(() => {
              setShowUploadProgressModal(false);
              
              // 성공 메시지 표시
              console.log('[제출 완료] 신청서 제출 및 파일 업로드 완료, onSave 함수 호출');
              onSave(formDataWithFileNames);
              setIsEditMode(false);
              
              // 성공 모달 표시
              setModalActionType('submit');
              setShowSaveConfirmModal(true);
            }, 1000);
          }
        } else {
          // 업로드할 파일이 없는 경우 바로 완료 처리
          onSave(formDataWithFileNames);
          setIsEditMode(false);
          // 성공 모달 표시
          setModalActionType('submit');
          setShowSaveConfirmModal(true);
        }
      } else {
        // 실패 메시지 표시
        alert('신청서 제출에 실패했습니다.');
      }
    } catch (error) {
      console.error('신청서 제출 중 오류 발생:', error);
      alert('신청서 제출 중 오류가 발생했습니다.');
    }
  };

  const handleConsentModalClose = () => {
    setShowConsentModal(false);
  };

  const handleDraftSave = async () => {
    try {
      // For temporary saves, privacy agreement check is not required
      const tempData = {
        ...formData,
        status: 'temp', // Indicate temporary save status
        ...getFormattedFileNames(selectedFiles)
      };

      // 초안 저장 시 삭제된 파일 정보 처리 - 명시적으로 로그 추가
      if (hasDraft) {
        if (selectedFiles.applicationForm === null && !tempData.hasOwnProperty('applicationForm')) {
          console.log('[초안 저장] applicationForm 필드가 삭제됨, 빈 문자열로 설정');
          tempData.applicationForm = '';
        }
        
        if (selectedFiles.irPitchDeck === null && !tempData.hasOwnProperty('irPitchDeck')) {
          console.log('[초안 저장] irPitchDeck 필드가 삭제됨, 빈 문자열로 설정');
          tempData.irPitchDeck = '';
        }
        
        if (selectedFiles.additionalDocuments === null && !tempData.hasOwnProperty('additionalDocuments')) {
          console.log('[초안 저장] additionalDocuments 필드가 삭제됨, 빈 문자열로 설정');
          tempData.additionalDocuments = '';
        }
      }

      console.log('[초안 저장] 초안 데이터:', tempData);

      let apiResponse;
      let shouldShowModal = true; // 모달 표시 여부를 제어하는 플래그

      if (mode === 'create') {
        // 새로운 초안 생성
        console.log('[API 요청] /user/apply/draft/create 호출 시작');
        apiResponse = await apiClient.post('/user/apply/draft/create', {
          email: userEmail,
          data: tempData
        });
        
        console.log('[API 응답] /user/apply/draft/create 응답:', apiResponse.data);
      } else if (hasDraft && mode === 'edit') {
        // 기존 초안 업데이트
        console.log('[API 요청] /user/apply/draft/update 호출 시작');
        apiResponse = await apiClient.post('/user/apply/draft/update', {
          email: userEmail,
          data: tempData
        });
        
        console.log('[API 응답] /user/apply/draft/update 응답:', apiResponse.data);
      } else {
        // 기존 로직 유지 (API 연결 없이 상위 컴포넌트의 onSave 사용)
        console.log('[로컬 처리] API 연결 없이 초안 처리');
        onSave(tempData);
        setIsEditMode(false);
        // 성공 모달 표시
        setModalActionType('draft');
        setShowSaveConfirmModal(true);
        return;
      }
      
      // API 응답 확인
      if (apiResponse && apiResponse.data && apiResponse.data.success) {
        console.log('[초안 저장 성공] 초안 저장 완료, 파일 업로드 처리 시작');
        
        // 파일 업로드 처리
        const applicationFormInput = document.getElementById('applicationForm');
        const irPitchDeckInput = document.getElementById('irPitchDeck');
        const additionalDocumentsInput = document.getElementById('additionalDocuments');
        
        // 업로드할 파일 목록 생성
        const filesToUpload = [];
        
        if (applicationFormInput && applicationFormInput.files.length > 0) {
          filesToUpload.push({
            id: 'applicationForm',
            inputElement: applicationFormInput,
            file: applicationFormInput.files[0],
            name: 'Application Form',
            progress: 0,
            uploaded: false
          });
        }
        
        if (irPitchDeckInput && irPitchDeckInput.files.length > 0) {
          filesToUpload.push({
            id: 'irPitchDeck',
            inputElement: irPitchDeckInput,
            file: irPitchDeckInput.files[0],
            name: 'IR Pitch Deck',
            progress: 0,
            uploaded: false
          });
        }
        
        if (additionalDocumentsInput && additionalDocumentsInput.files.length > 0) {
          filesToUpload.push({
            id: 'additionalDocuments',
            inputElement: additionalDocumentsInput,
            file: additionalDocumentsInput.files[0],
            name: 'Additional Documents',
            progress: 0,
            uploaded: false
          });
        }
        
        console.log('[초안 파일 업로드] 업로드할 파일 목록:', filesToUpload.map(f => `${f.name} (${f.file.size} bytes)`));
        
        // 업로드할 파일이 있는 경우에만 진행
        if (filesToUpload.length > 0) {
          // 파일 업로드가 있으면 먼저 모달 표시
          setModalActionType('draft');
          setShowSaveConfirmModal(true);
          shouldShowModal = false; // 파일 업로드 후 모달을 다시 표시하지 않도록 플래그 설정
          
          setFilesProgress(filesToUpload.map(file => ({ name: file.name, progress: 0 })));
          setTotalProgress(0);
          setShowUploadProgressModal(true);
          setIsUploading(true);
          
          try {
            // 파일들의 정보 준비
            const fileData = {};
            const fileObjects = {};
            
            // 각 파일 정보 수집
            filesToUpload.forEach(fileInfo => {
              const file = fileInfo.file;
              const fileExtension = file.name.split('.').pop().toLowerCase();
              fileData[fileInfo.id] = `${fileInfo.id}.${fileExtension}`;
              fileObjects[fileInfo.id] = {
                file: file,
                extension: fileExtension,
                contentType: allowedAttachmentsFileTypes[fileExtension] || 'application/octet-stream',
                name: fileInfo.name
              };
            });
            
            // 단일 API 호출로 모든 파일의 업로드 URL 요청
            console.log('[초안 파일 업로드] 파일 업로드 URL 요청');
            const fileUploadResponse = await apiClient.post('/user/apply/file/upload', {
              email: userEmail,
              ...fileData,
              isDraft: true // 초안 파일임을 표시
            });
            
            console.log('[초안 파일 업로드] 응답:', fileUploadResponse.data);
            
            // 업로드 결과를 저장할 변수
            let uploadResults = [];
            let fileTypes = [];
            
            // 파일 업로드 URL이 있는 경우 파일 업로드 진행
            if (fileUploadResponse && fileUploadResponse.data && fileUploadResponse.data.urls) {
              // 응답에서 각 파일 타입에 대한 URL 추출
              const uploadUrls = fileUploadResponse.data.urls;
              const uploadPromises = [];
              
              // 먼저 fileTypes 초기화 - 업로드 URL에서 타입 추출 (성공 여부와 관계없이)
              fileTypes = Object.keys(uploadUrls);
              console.log('[초안 파일 업로드] 업로드될 파일 타입 목록 (URL 기준):', fileTypes);
              
              // 각 파일 타입에 대해 업로드 진행
              Object.keys(uploadUrls).forEach((fileType, index) => {
                if (uploadUrls[fileType] && fileObjects[fileType]) {
                  console.log(`[초안 파일 업로드] ${fileType} 업로드 준비 중`);
                  // uploadUrls[fileType]는 객체 구조임(url, key, contentType 등 포함)
                  const uploadInfo = uploadUrls[fileType];
                  const uploadUrl = uploadInfo.url; // url 속성 사용
                  const fileObject = fileObjects[fileType];
                  
                  // S3 pre-signed URL의 만료 시간 계산 및 출력
                  if (uploadUrl && uploadUrl.includes('?')) {
                    const urlParams = new URLSearchParams(uploadUrl.split('?')[1]);
                    const expiresParam = urlParams.get('X-Amz-Expires');
                    const dateParam = urlParams.get('X-Amz-Date');
                    
                    if (expiresParam && dateParam) {
                      try {
                        // X-Amz-Date 형식은 보통 YYYYMMDDTHHMMSSZ 형식 (예: 20240506T120000Z)
                        const year = dateParam.substring(0, 4);
                        const month = dateParam.substring(4, 6);
                        const day = dateParam.substring(6, 8);
                        const hour = dateParam.substring(9, 11);
                        const minute = dateParam.substring(11, 13);
                        const second = dateParam.substring(13, 15);
                        
                        // UTC 기준 날짜 생성
                        const baseDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
                        
                        // 만료 시간(초) 추가
                        const expiryDate = new Date(baseDate.getTime() + parseInt(expiresParam) * 1000);
                        
                        // 현재 시간
                        const now = new Date();
                        
                        // 로컬 시간으로 출력
                        console.log(`[초안 S3 URL 만료 정보] ${fileObject.name}`);
                        console.log(`- 현재 시간: ${now.toLocaleString()}`);
                        console.log(`- URL 생성 시간: ${baseDate.toLocaleString()}`);
                        console.log(`- URL 만료 시간: ${expiryDate.toLocaleString()}`);
                        console.log(`- 남은 시간: ${Math.round((expiryDate - now) / 1000)}초`);
                      } catch (error) {
                        console.error('[초안 S3 URL 만료 시간 계산 오류]', error);
                      }
                    }
                  }
                  
                  // 파일 이름 처리 (원본 파일 확장자 유지)
                  const newFileName = `${fileType}.${fileObject.extension}`;
                  console.log(`[초안 파일 업로드] ${fileObject.name} - 변환된 파일명: ${newFileName}`);
                  
                  // 파일 객체 디버깅
                  console.log(`[업로드 디버깅] 파일 객체:`, {
                    originalFile: fileObject.file,
                    name: fileObject.file.name,
                    size: fileObject.file.size,
                    type: fileObject.file.type,
                    lastModified: fileObject.file.lastModified
                  });
                  
                  // 파일 객체 생성 방식 변경 - Blob 사용
                  // const renamedFile = new File([fileObject.file], newFileName, { type: fileObject.contentType });
                  // 파일 객체의 바이너리 데이터 직접 접근
                  const reader = new FileReader();
                  
                  // Promise를 미리 생성하고 배열에 추가
                  const uploadPromise = new Promise((resolve, reject) => {
                    reader.onload = function(e) {
                      const fileContent = e.target.result; // ArrayBuffer 형태의 파일 내용
                      console.log(`[업로드 디버깅] 파일 내용 로드됨: ${fileContent.byteLength} 바이트`);
                      
                      // Blob 객체로 변환하여 업로드
                      const blob = new Blob([fileContent], { type: fileObject.contentType });
                      console.log(`[업로드 디버깅] Blob 생성됨:`, {
                        size: blob.size,
                        type: blob.type
                      });
                      
                      // XMLHttpRequest를 사용하여 업로드
                      const xhr = new XMLHttpRequest();
                      
                      // 업로드 진행 상태 이벤트 리스너
                      xhr.upload.addEventListener('progress', (event) => {
                        if (event.lengthComputable) {
                          const percentComplete = (event.loaded / event.total) * 100;
                          
                          // 특정 파일 진행 상태 업데이트
                          setFilesProgress(prev => {
                            const newProgress = [...prev];
                            const fileIndex = filesToUpload.findIndex(f => f.id === fileType);
                            if (fileIndex !== -1) {
                              newProgress[fileIndex] = { name: fileObject.name, progress: percentComplete };
                            }
                            return newProgress;
                          });
                          
                          // 전체 진행 상태 계산 및 업데이트
                          setTotalProgress(prev => {
                            // 모든 파일의 평균 진행 상태 계산
                            const allProgress = filesProgress.map(f => f.progress);
                            const fileIndex = filesToUpload.findIndex(f => f.id === fileType);
                            if (fileIndex !== -1) {
                              allProgress[fileIndex] = percentComplete;
                            }
                            const avgProgress = allProgress.reduce((sum, curr) => sum + curr, 0) / allProgress.length;
                            return avgProgress;
                          });
                        }
                      });
                      
                      // 업로드 완료 이벤트 리스너
                      xhr.addEventListener('load', () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                          console.log(`[초안 파일 업로드 성공] ${fileObject.name}`);
                          
                          // 디버깅: 응답 상세 정보
                          console.log(`[업로드 디버깅] 응답 상세:`, {
                            status: xhr.status,
                            statusText: xhr.statusText,
                            responseText: xhr.responseText
                          });
                          
                          // 특정 파일의 진행 상태를 100%로 설정
                          setFilesProgress(prev => {
                            const newProgress = [...prev];
                            const fileIndex = filesToUpload.findIndex(f => f.id === fileType);
                            if (fileIndex !== -1) {
                              newProgress[fileIndex] = { name: fileObject.name, progress: 100 };
                            }
                            return newProgress;
                          });
                          
                          resolve({
                            success: true,
                            fileType: fileType,
                            uploadInfo: uploadInfo
                          });
                        } else {
                          console.error(`[초안 파일 업로드 실패] ${fileObject.name} - 응답 상태: ${xhr.status}, 에러: ${xhr.statusText}`);
                          reject(new Error(xhr.statusText));
                        }
                      });
                      
                      // 에러 이벤트 리스너
                      xhr.addEventListener('error', () => {
                        console.error(`[초안 파일 업로드 오류] ${fileObject.name} - 네트워크 오류 발생`);
                        reject(new Error('Network error'));
                      });
                      
                      console.log(`[초안 파일 업로드] ${fileObject.name} - S3 업로드 시작, URL: ${uploadUrl}`);
                      
                      // 업로드 요청 설정
                      xhr.open('PUT', uploadUrl, true);
                      xhr.setRequestHeader('Content-Type', fileObject.contentType);
                      
                      // CORS 관련 문제 완화 시도
                      xhr.withCredentials = false; // CORS 요청 시 자격 증명 전송 안 함
                      
                      // 디버깅을 위한 요청 세부 정보 출력
                      console.log(`[업로드 디버깅] 요청 정보:`, {
                        method: 'PUT',
                        url: uploadUrl,
                        contentType: fileObject.contentType,
                        fileSize: blob.size,
                        fileName: newFileName,
                        withCredentials: xhr.withCredentials
                      });
                      
                      // 업로드 시작 - File 객체 대신 Blob 사용
                      xhr.send(blob);
                    };
                    
                    reader.onerror = function() {
                      console.error(`[초안 파일 업로드] ${fileObject.name} - 파일 읽기 오류 발생`);
                      reject(new Error('File read error'));
                    };
                    
                    // 파일 읽기 시작
                    reader.readAsArrayBuffer(fileObject.file);
                  });
                  
                  console.log(`[초안 파일 업로드] ${fileType} Promise 배열에 추가됨`);
                  uploadPromises.push(uploadPromise);
                }
              });
              
              // Promise 배열 상태 확인 로그
              console.log('[초안 파일 업로드] Promise 배열 길이:', uploadPromises.length);
              
              // 모든 파일 업로드 완료 대기
              console.log('[초안 파일 업로드] 모든 파일 업로드 Promise 시작');
              uploadResults = await Promise.all(uploadPromises);
              console.log('[초안 파일 업로드] 모든 파일 업로드 완료, 결과:', uploadResults);
              
              // 파일 타입 목록 생성
              if (uploadResults.length > 0) {
                // 복사해야 할 파일 타입 목록
                fileTypes = uploadResults
                  .filter(result => result.success)
                  .map(result => result.fileType);
              }
            } else {
              // URLs가 없는 경우, 업로드할 파일 목록에서 fileTypes 추출
              console.log('[초안 파일 업로드] URLs가 없거나 응답 형식이 다름. 파일 타입만 추출.');
              fileTypes = Object.keys(fileObjects);
              console.log('[초안 파일 업로드] 추출된 파일 타입:', fileTypes);
            }
            
            // 파일 복사 처리 - URLs가 있든 없든 실행
            if (fileTypes.length > 0) {
              // 파일 복사 진행상태 업데이트
              setFilesProgress(prev => {
                return prev.map(file => ({
                  ...file,
                  name: `${file.name} (copying...)`
                }));
              });
              
              // 파일 복사 API 호출을 위한 데이터 준비
              const formattedFileNames = getFormattedFileNames(selectedFiles);
              const copyRequestData = {
                email: userEmail,
                fileTypes: fileTypes,
                isDraft: true,
                ...formattedFileNames
              };
              
              // 요청 데이터 로깅 (디버깅용)
              console.log('[초안 파일 복사] 요청 데이터 상세:', JSON.stringify(copyRequestData, null, 2));
              console.log('[초안 파일 복사] /user/apply/file/copy 호출 시작, 파일 타입:', fileTypes);
              
              try {
                // 복사 API 호출
                const copyResponse = await apiClient.post('/user/apply/file/copy', copyRequestData);
                console.log('[초안 파일 복사] /user/apply/file/copy 응답 성공:', copyResponse.data);
              } catch (copyError) {
                console.error('[초안 파일 복사] /user/apply/file/copy 호출 실패:', copyError);
                // 복사 실패해도 계속 진행
              }
            } else {
              console.log('[초안 파일 복사] 복사할 파일 타입이 없음');
            }
          } catch (error) {
            console.error('[초안 파일 업로드/복사 오류] 처리 중 예외 발생:', error);
          } finally {
            // 업로드 완료 후 상태 업데이트
            setIsUploading(false);
            // 모든 업로드 완료 후 진행 상태 모달을 약간 더 표시한 후 닫기
            setTimeout(() => {
              setShowUploadProgressModal(false);
              
              // 성공 메시지 표시
              console.log('[초안 처리 완료] 초안 저장 및 파일 업로드 완료, onSave 함수 호출');
              onSave(tempData);
              setIsEditMode(false);
              
              // 이미 모달이 표시된 상태이므로 여기서는 모달을 표시하지 않음
            }, 1000);
          }
        } else {
          // 업로드할 파일이 없는 경우 바로 완료 처리
          onSave(tempData);
          setIsEditMode(false);
          
          // 모달을 표시해야 하는 경우에만 표시
          if (shouldShowModal) {
            setModalActionType('draft');
            setShowSaveConfirmModal(true);
          }
        }
      } else {
        // 실패 메시지 표시
        console.error('[초안 API 실패] 초안 저장 실패, 응답:', apiResponse);
        alert('초안 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('[예외 발생] 초안 저장 중 오류:', error);
      alert('초안 저장 중 오류가 발생했습니다.');
    }
  };

  const handleCancelClick = () => {
    if (window.confirm('Your changes will not be saved. Are you sure you want to cancel?')) {
      onCancel();
      setIsEditMode(false);
    }
  };

  const toggleIndustryDropdown = () => {
    setIndustryDropdownOpen(!industryDropdownOpen);
  };
  
  const toggleEntityDropdown = () => {
    setEntityDropdownOpen(!entityDropdownOpen);
  };
  
  const toggleGenderDropdown = () => {
    setGenderDropdownOpen(!genderDropdownOpen);
  };
  
  const selectIndustry = (industry) => {
    setFormData(prev => ({
      ...prev,
      industry
    }));
    setIndustryDropdownOpen(false);
  };
  
  const selectEntityStatus = (entityStatus) => {
    setFormData(prev => ({
      ...prev,
      entityStatus
    }));
    setEntityDropdownOpen(false);
  };
  
  const selectGender = (gender) => {
    setFormData(prevData => ({
      ...prevData,
      gender
    }));
    setGenderDropdownOpen(false);
  };

  const handleDateChange = (date, name) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };
  
  // 날짜 직접 입력 처리 함수
  const handleDateInputChange = (e, name) => {
    const inputValue = e.target.value;
    // 입력된 값이 유효한 날짜 형식인지 확인
    const dateValue = new Date(inputValue);
    
    if (!isNaN(dateValue.getTime())) {
      // 유효한 날짜인 경우
      setFormData(prev => ({
        ...prev,
        [name]: dateValue
      }));
    } else {
      // 단순 텍스트 입력 중인 경우는 state 업데이트하지 않음
      // 사용자가 입력을 완료할 때까지 기다림
    }
  };
  
  // 커스텀 날짜 입력 컴포넌트
  const CustomDateInput = forwardRef(({ value, onClick, onChange, placeholder, disabled }, ref) => (
    <div className="custom-date-input-container">
      <IoCalendarOutline className="custom-date-icon custom-date-icon-left" onClick={disabled ? undefined : onClick} />
      <input
        ref={ref}
        className="custom-date-input custom-date-input-with-left-icon"
        value={value}
        onChange={onChange}
        onClick={onClick}
        placeholder={placeholder}
        disabled={disabled}
      />
    </div>
  ));
  
  CustomDateInput.displayName = 'CustomDateInput';

  // 파일 클릭 처리 함수
  const handleFileClick = async (e, fileType) => {
    // 기존 파일이 있는 경우에만 실행
    if ((fileType === 'Application Form' && formData.applicationForm) || 
        (fileType === 'IR Pitch Deck' && formData.irPitchDeck) ||
        (fileType === 'Additional Documents' && formData.additionalDocuments)) {
      
      e.preventDefault();
      
      try {
        const fileFieldName = fileType === 'Application Form' ? 'applicationForm' : 
                             fileType === 'IR Pitch Deck' ? 'irPitchDeck' :
                             'additionalDocuments';
        const fileUrl = formData[fileFieldName];
        
        // URL이 유효한지 검사
        const isValidUrl = (url) => {
          try {
            new URL(url);
            return true;
          } catch (e) {
            return false;
          }
        };
        console.log(`[파일 열기] ${fileFieldName} 파일 URL: ${fileUrl}`);
        console.log(`[파일 열기] ${fileFieldName} 파일 URL 유효성 검사: ${fileFieldName}`);
        if (!isValidUrl(fileUrl)) {
          console.error('Invalid file URL');
          return;
        }
        
        // S3 pre-signed URL의 만료 시간 계산 및 출력
        if (fileUrl.includes('amazonaws.com') && fileUrl.includes('X-Amz-')) {
          const urlParams = new URLSearchParams(fileUrl.split('?')[1]);
          const expiresParam = urlParams.get('X-Amz-Expires');
          const dateParam = urlParams.get('X-Amz-Date');
          
          if (expiresParam && dateParam) {
            try {
              // X-Amz-Date 형식은 보통 YYYYMMDDTHHMMSSZ 형식 (예: 20240506T120000Z)
              const year = dateParam.substring(0, 4);
              const month = dateParam.substring(4, 6);
              const day = dateParam.substring(6, 8);
              const hour = dateParam.substring(9, 11);
              const minute = dateParam.substring(11, 13);
              const second = dateParam.substring(13, 15);
              
              // UTC 기준 날짜 생성
              const baseDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);
              
              // 만료 시간(초) 추가
              const expiryDate = new Date(baseDate.getTime() + parseInt(expiresParam) * 1000);
              
              // 현재 시간
              const now = new Date();
              
              // 로컬 시간으로 출력
              console.log(`[파일 열기 S3 URL 만료 정보] ${fileType}`);
              console.log(`- 현재 시간: ${now.toLocaleString()}`);
              console.log(`- URL 생성 시간: ${baseDate.toLocaleString()}`);
              console.log(`- URL 만료 시간: ${expiryDate.toLocaleString()}`);
              console.log(`- 남은 시간: ${Math.round((expiryDate - now) / 1000)}초`);
              
              // URL이 만료되었는지 시간으로 판단
              if (now > expiryDate) {
                console.log(`[파일 열기] ${fileFieldName} URL이 만료됨 (시간 기준), 새 URL 요청`);
                // 아래의 URL 재발급 코드가 실행됨
              }
            } catch (error) {
              console.error('[파일 열기 S3 URL 만료 시간 계산 오류]', error);
            }
          }
        }
        
        // URL 만료 확인 (간단한 방법으로는 fetch 요청의 헤더를 확인)
        const checkUrlExpiration = async (url) => {
          try {
            const response = await fetch(url, { method: 'HEAD' });
            return response.ok;
          } catch (error) {
            console.error('Error checking URL:', error);
            return false;
          }
        };
        
        // 테스트 목적으로 URL 만료를 강제로 시뮬레이션할 수 있는 플래그 추가
        // 개발 중에만 사용하고 프로덕션에서는 false로 설정해야 함
        const FORCE_URL_EXPIRY_TEST = false;
        
        // URL 유효성 체크
        let isUrlValid = await checkUrlExpiration(fileUrl);
        
        // 테스트 목적으로 URL 만료 강제 설정
        if (FORCE_URL_EXPIRY_TEST) {
          console.log('[테스트] URL 만료 강제 시뮬레이션');
          isUrlValid = false;
        }
        
        if (isUrlValid) {
          // URL이 유효하면 해당 URL로 새 창 열기
          window.open(fileUrl, '_blank');
        } else {
          // URL이 만료되었으면 새 URL 요청
          console.log(`[파일 열기] ${fileFieldName} URL이 만료됨, 새 URL 요청 ----- : `, selectedFiles[fileFieldName]);
          
          // fileType 대신 실제 필드명(applicationForm 또는 irPitchDeck)을 보냄
          const response = await apiClient.post('/user/apply/file/read', {
            email: userEmail,
            [fileFieldName]: `${selectedFiles[fileFieldName]}` // fileType 대신 실제 필드명을 키로 사용
          });
          
          console.log('[파일 열기] API 응답:', response.data);
          
          if (response.data?.success && response.data?.urls?.[fileFieldName]) {
            // 상태 업데이트
            setFormData(prev => ({
              ...prev,
              [fileFieldName]: response.data.urls[fileFieldName]
            }));
            
            // 새 URL로 창 열기
            window.open(response.data.urls[fileFieldName], '_blank');
          } else {
            console.error('Failed to get new file URL. Response:', response.data);
          }
        }
      } catch (error) {
        console.error('Error handling file click:', error);
      }
    }
  };

  // 파일 선택 처리 함수
  const handleFileSelectClick = (e, fileType) => {
    const fileId = fileType === 'Application Form' ? 'applicationForm' : 
                    fileType === 'IR Pitch Deck' ? 'irPitchDeck' :
                    'additionalDocuments';
    
    // 이미 로딩 중이면 중복 클릭 방지
    if (isFileSelectLoading[fileId]) {
      e.preventDefault();
      return;
    }
    
    // 기존 파일이 있는지 확인
    if ((fileType === 'Application Form' && formData.applicationForm) || 
        (fileType === 'IR Pitch Deck' && formData.irPitchDeck) ||
        (fileType === 'Additional Documents' && formData.additionalDocuments)) {
      
      e.preventDefault();
      
      // 로딩 상태 설정
      setIsFileSelectLoading(prev => ({
        ...prev,
        [fileId]: true
      }));
      
      // 기존 타임아웃 제거 (있는 경우)
      if (fileSelectTimeoutRefs.current[fileId]) {
        clearTimeout(fileSelectTimeoutRefs.current[fileId]);
      }
      
      // 현재 선택한 파일 타입 저장
      setCurrentFileType(fileType);
      
      // 모달 표시 (디바운스 적용)
      fileSelectTimeoutRefs.current[fileId] = setTimeout(() => {
        setShowOverwriteModal(true);
        // 클릭한 입력 요소 저장
        setPendingFileInput(fileType === 'Application Form' ? applicationFormInputRef.current : 
                            fileType === 'IR Pitch Deck' ? irPitchDeckInputRef.current :
                            additionalDocumentsInputRef.current);
        
        // 로딩 상태 해제
        setIsFileSelectLoading(prev => ({
          ...prev,
          [fileId]: false
        }));
      }, 300);
    } else {
      // 기존 파일이 없는 경우 직접 파일 선택 다이얼로그 열기
      const inputRef = fileType === 'Application Form' ? applicationFormInputRef.current : 
                      fileType === 'IR Pitch Deck' ? irPitchDeckInputRef.current :
                      additionalDocumentsInputRef.current;
      
      // 로딩 상태 설정
      setIsFileSelectLoading(prev => ({
        ...prev,
        [fileId]: true
      }));
      
      // 기존 타임아웃 제거 (있는 경우)
      if (fileSelectTimeoutRefs.current[fileId]) {
        clearTimeout(fileSelectTimeoutRefs.current[fileId]);
      }
      
      // 클릭 이벤트 디바운스
      fileSelectTimeoutRefs.current[fileId] = setTimeout(() => {
        if (inputRef) {
          inputRef.click();
        }
        
        // 로딩 상태 해제
        setIsFileSelectLoading(prev => ({
          ...prev,
          [fileId]: false
        }));
      }, 300);
    }
  };

  // 컴포넌트 언마운트 시 타임아웃 정리
  useEffect(() => {
    return () => {
      Object.keys(fileSelectTimeoutRefs.current).forEach(key => {
        if (fileSelectTimeoutRefs.current[key]) {
          clearTimeout(fileSelectTimeoutRefs.current[key]);
        }
      });
    };
  }, []);

  // 덮어쓰기 확인 모달에서 확인 버튼 클릭 처리
  const handleOverwriteConfirm = () => {
    // 모달 닫기
    setShowOverwriteModal(false);
    
    // 저장된 입력 요소가 있으면 클릭을 시뮬레이션
    if (pendingFileInput) {
      pendingFileInput.click();
    }
    
    // 상태 초기화
    setPendingFileInput(null);
  };

  // 덮어쓰기 취소 처리
  const handleOverwriteCancel = () => {
    setShowOverwriteModal(false);
    setPendingFileInput(null);
  };

  // 파일 크기를 읽기 쉬운 형식으로 변환하는 함수
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    // 소수점 첫째자리까지만 표기
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // 파일 선택 처리 함수
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    
    if (files && files.length > 0) {
      const file = files[0];
      
      // 파일 크기가 0인지 확인
      if (file.size === 0) {
        setZeroSizeFileName(file.name);
        setShowZeroSizeModal(true);
        // 파일 선택 초기화
        e.target.value = '';
        return;
      }
      
      setSelectedFiles(prev => ({
        ...prev,
        [name]: file.name
      }));
      
      // 파일 크기 저장
      setFileSizes(prev => ({
        ...prev,
        [name]: formatFileSize(file.size)
      }));
      
      // 수정 모드 활성화 - hasApplication 또는 hasDraft가 true인 경우에만
      if (hasApplication || hasDraft) {
        setIsEditMode(true);
      }
    }
  };

  // 파일 삭제 준비 함수 (확인 모달 표시)
  const handleDeleteFileClick = (name) => {
    setFileToDelete(name);
    setShowDeleteConfirmModal(true);
  };

  // 파일 삭제 처리 함수 (확인 후 실행)
  const handleFileDelete = () => {
    const name = fileToDelete;
    
    console.log(`[파일 삭제] ${name} 파일 삭제 시작`);
    
    // 파일 선택 초기화
    if (name === 'applicationForm') {
      applicationFormInputRef.current.value = '';
    } else if (name === 'irPitchDeck') {
      irPitchDeckInputRef.current.value = '';
    } else if (name === 'additionalDocuments') {
      additionalDocumentsInputRef.current.value = '';
    }
    
    // 선택된 파일 정보 초기화
    setSelectedFiles(prev => ({
      ...prev,
      [name]: null
    }));
    
    // 파일 크기 정보 초기화
    setFileSizes(prev => ({
      ...prev,
      [name]: null
    }));
    
    // hasDraft가 true인 경우, formData에서 해당 파일 필드를 빈 문자열로 설정
    if (hasDraft) {
      console.log(`[파일 삭제] hasDraft가 true이므로 ${name} 필드를 빈 문자열로 설정`);
      setFormData(prev => ({
        ...prev,
        [name]: '' // 빈 문자열로 설정하여 서버에서 필드가 업데이트되도록 함
      }));
    }
    
    // 모달 닫기
    setShowDeleteConfirmModal(false);
    
    // 수정 모드 활성화 - hasApplication 또는 hasDraft가 true인 경우에만
    if (hasApplication || hasDraft) {
      setIsEditMode(true);
    }
    
    console.log(`[파일 삭제] ${name} 파일 삭제 완료`);
  };

  // 파일 삭제 취소 처리
  const handleDeleteCancel = () => {
    setShowDeleteConfirmModal(false);
    setFileToDelete('');
  };

  // 파일 크기가 0인 경우 표시할 모달
  const ZeroSizeFileModal = () => {
    return (
      <div className={`modal ${showZeroSizeModal ? 'show' : ''}`} style={{ display: showZeroSizeModal ? 'block' : 'none' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">File Size Error</h5>
              <button type="button" className="close" onClick={() => setShowZeroSizeModal(false)}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="file-error-message">
                <div className="file-error-icon">!</div>
                <p>The file has zero size. Please check your attachment and try again.</p>
                {zeroSizeFileName && <p className="file-name-error">{zeroSizeFileName}</p>}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary" onClick={() => setShowZeroSizeModal(false)}>OK</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 파일 삭제 확인 모달
  const DeleteConfirmModal = () => {
    // 삭제할 파일 이름 가져오기
    const fileDisplayName = fileToDelete === 'applicationForm' ? 
      selectedFiles.applicationForm : 
      (fileToDelete === 'irPitchDeck' ? selectedFiles.irPitchDeck :
      (fileToDelete === 'additionalDocuments' ? selectedFiles.additionalDocuments : ''));
      
    return (
      <div className={`modal ${showDeleteConfirmModal ? 'show' : ''}`} style={{ display: showDeleteConfirmModal ? 'block' : 'none' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Delete File Confirmation</h5>
              <button type="button" className="close" onClick={handleDeleteCancel}>
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="file-delete-message">
                <FaTrashAlt className="file-delete-icon" />
                <p>Are you sure you want to delete this file?</p>
                {fileDisplayName && <p className="file-name-to-delete">{fileDisplayName}</p>}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleDeleteCancel}>Cancel</button>
              <button type="button" className="btn btn-danger" onClick={handleFileDelete}>Delete</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 토스트 모달 닫기 함수
  const handleValidationToastClose = () => {
    console.log('[디버깅] 토스트 닫기 함수 호출됨');
    setShowValidationToast(false);
  };

  // ValidationToastModal 렌더링 상태 모니터링
  useEffect(() => {
    console.log('[디버깅] 토스트 상태 변경:', {
      showValidationToast,
      validationMessage,
      invalidElement: invalidElement ? `${invalidElement.id || invalidElement.name} 요소` : 'null'
    });
    
    // 토스트가 표시되면 3초 후 자동으로 닫히도록 설정
    if (showValidationToast) {
      const timer = setTimeout(() => {
        console.log('[디버깅] 토스트 자동 닫기 타이머 실행');
        setShowValidationToast(false);
      }, 2000);
      
      // 컴포넌트 언마운트 또는 상태 변경 시 타이머 정리
      return () => clearTimeout(timer);
    }
  }, [showValidationToast, validationMessage, invalidElement]);
  
  // 토스트 메시지를 표시하는 헬퍼 함수
  const showToast = (message, element) => {
    // 메시지와 대상 요소 설정
    setValidationMessage(message);
    setInvalidElement(element);
    
    // 기존 토스트를 먼저 숨긴 후 새 토스트 표시
    setShowValidationToast(false);
    
    // 약간의 지연 후 새 토스트 표시 (상태 업데이트가 올바르게 처리되도록)
    setTimeout(() => {
      console.log('[디버깅] 새 토스트 표시:', message);
      setShowValidationToast(true);
    }, 10);
    
    // 요소가 있으면 해당 요소로 스크롤 및 포커스
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // 입력 요소인 경우 포커스 설정
      if (element.focus && typeof element.focus === 'function') {
        element.focus();
      }
    }
  };

  // 내비게이션 모달 처리
  const handleNavigationClick = (path) => {
    if (mode === 'create' || (hasDraft && mode === 'edit') || (hasApplication && mode === 'edit')) {
      setNavigationTarget(path);
      setModalActionType('navigate');
      setShowSaveConfirmModal(true);
    } else {
      navigate(path);
    }
  };

  const handleNavigationConfirm = () => {
    setShowSaveConfirmModal(false);
    if (navigationTarget) {
      navigate(navigationTarget);
    }
  };

  const handleNavigationCancel = () => {
    setShowSaveConfirmModal(false);
    setNavigationTarget(null);
  };

  // 내비게이션 확인 모달 삭제

  const handleParticipantInfoClick = () => {
    setShowParticipantInfoModal(true);
  };
  
  const handleParticipantInfoClose = () => {
    setShowParticipantInfoModal(false);
  };
  
  const ParticipantInfoModal = () => {
    return (
      <div 
        className="modal-overlay" 
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            e.stopPropagation();
          }
        }}
      >
        <div className="modal-content">
          <h2>Participant Information</h2>
          <p>
            Participants : must be a non-Korean member of the company, who can make major decisions throughout the program
          </p>
          <div className="modal-buttons">
            <button 
              className="modal-button save" 
              onClick={handleParticipantInfoClose}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="application-form-outer-container">
      <ApplyTitleHeader 
        title={['K-STARTUP', 'GRAND CHALLENGE 2025']} 
        onClick={() => handleNavigationClick('/')} 
      />
      <div className="application-form-container">
        <div className="application-form-header">
          <div className="application-form-header-left-section">
            <h2>My Application</h2>
          </div>
          {mode === 'edit' && hasDraft && (
            <div className="draft-notice">
              <p>{NOTIFICATION_MESSAGES.DRAFT_EDITING}</p>
            </div>
          )}
          <div className="button-group">
            <ApplyAuth
              isLoggedIn={isLoggedIn}
              userEmail={userEmail}
              onLogout={onLogout}
            />
          </div>
        </div>

        {mode === 'edit' && hasApplication && <div className="submission-notice">
          <p>{NOTIFICATION_MESSAGES.SUBMITTED_EDITING}</p>
        </div>}
        
        <div className="application-form">
          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            <div className="application-info">
              <div className="application-form-info-group">
                <label htmlFor="teamName">Team(Company) Name</label>
                <input
                  type="text"
                  id="teamName"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="entityStatus">Entity Status</label>
                <div className="custom-dropdown" ref={entityDropdownRef}>
                  <button 
                    className="dropdown-button"
                    type="button" 
                    onClick={toggleEntityDropdown}
                  >
                    {formData.entityStatus || 'Select entity status'}
                    <span className="dropdown-arrow">▼</span>
                  </button>
                  {entityDropdownOpen && (
                    <ul className="dropdown-list">
                      {ENTITY_STATUS_OPTIONS.map((option) => (
                        <li key={option.value}>
                          <button 
                            type="button"
                            className="dropdown-item" 
                            onClick={() => selectEntityStatus(option.value)}
                          >
                            {option.value}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Hidden select for form submission */}
                  <select
                    id="entityStatus"
                    name="entityStatus"
                    value={formData.entityStatus}
                    onChange={handleChange}
                    required
                    className="hidden-select"
                  >
                    <option value="">Select entity status</option>
                    {ENTITY_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="application-form-info-group">
                <label htmlFor="establishedDate">Established Date</label>
                <DatePicker 
                  id="establishedDate"
                  name="establishedDate"
                  selected={formData.establishedDate ? new Date(formData.establishedDate) : null}
                  onChange={(date) => handleDateChange(date, 'establishedDate')}
                  onChangeRaw={(e) => handleDateInputChange(e, 'establishedDate')}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select date (yyyy-mm-dd)"
                  className="date-picker"
                  required={formData.entityStatus !== 'Pre-established'}
                  disabled={formData.entityStatus === 'Pre-established'}
                  customInput={<CustomDateInput />}
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="location">Headquarters Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="ceo">CEO</label>
                <input
                  type="text"
                  id="ceo"
                  name="ceo"
                  value={formData.ceo}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="gender">Gender</label>
                <div className="custom-dropdown" ref={genderDropdownRef}>
                  <button 
                    className="dropdown-button"
                    type="button" 
                    onClick={toggleGenderDropdown}
                  >
                    {formData.gender || 'Select Gender'}
                    <span className="dropdown-arrow">▼</span>
                  </button>
                  {genderDropdownOpen && (
                    <ul className="dropdown-list">
                      <li>
                        <button 
                          type="button"
                          className="dropdown-item" 
                          onClick={() => selectGender('Male')}
                        >
                          Male
                        </button>
                      </li>
                      <li>
                        <button 
                          type="button"
                          className="dropdown-item" 
                          onClick={() => selectGender('Female')}
                        >
                          Female
                        </button>
                      </li>
                    </ul>
                  )}
                  {/* Hidden select for form submission */}
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender || ''}
                    onChange={handleChange}
                    required
                    className="hidden-select"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              <div className="application-form-info-group">
                <label htmlFor="participant">
                  Participant
                  <AiOutlineInfoCircle 
                    style={{ 
                      marginLeft: '5px', 
                      cursor: 'pointer',
                      verticalAlign: 'middle',
                      color: '#2563eb'
                    }} 
                    onClick={handleParticipantInfoClick}
                    size={16}
                  />
                </label>
                <input
                  type="text"
                  id="participant"
                  name="participant"
                  value={formData.participant}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="participantTitle">Participant's title</label>
                <input
                  type="text"
                  id="participantTitle"
                  name="participantTitle"
                  value={formData.participantTitle}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="participantNationality">Participant's nationality</label>
                <input
                  type="text"
                  id="participantNationality"
                  name="participantNationality"
                  value={formData.participantNationality}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="industry">Industry</label>
                <div className="custom-dropdown" ref={industryDropdownRef}>
                  <button 
                    className="dropdown-button"
                    type="button" 
                    onClick={toggleIndustryDropdown}
                  >
                    {formData.industry || 'Select Industry'}
                    <span className="dropdown-arrow">▼</span>
                  </button>
                  {industryDropdownOpen && (
                    <ul className="dropdown-list">
                      {industryList.map((industry, index) => (
                        <li key={index}>
                          <button 
                            type="button"
                            className="dropdown-item" 
                            onClick={() => selectIndustry(industry)}
                          >
                            {index + 1}) {industry}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Hidden select for form submission */}
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    required
                    className="hidden-select"
                  >
                    <option value="">Select Industry</option>
                    {industryList.map((industry, index) => (
                      <option key={index} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="application-form-info-group">
                <label htmlFor="businessItem">Business Item</label>
                <textarea
                  id="businessItem"
                  name="businessItem"
                  value={formData.businessItem}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="website">Website</label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https:// or http://"
                  className="input-with-small-placeholder"
                  // pattern="https?://.*"
                  // title="웹사이트 주소는 http:// 또는 https://로 시작해야 합니다"
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="socialMedia">Social Media</label>
                <input
                  type="url"
                  id="socialMedia"
                  name="socialMedia"
                  value={formData.socialMedia}
                  onChange={handleChange}
                  placeholder="https:// or http://"
                  className="input-with-small-placeholder"
                  // pattern="https?://.*"
                  // title="소셜미디어 주소는 http:// 또는 https://로 시작해야 합니다"
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="promotionalVideo">Promotional Video Link</label>
                <input
                  type="url"
                  id="promotionalVideo"
                  name="promotionalVideo"
                  value={formData.promotionalVideo}
                  onChange={handleChange}
                  placeholder="https:// or http://"
                  className="input-with-small-placeholder"
                  // pattern="https?://.*"
                  // title="비디오 링크는 http:// 또는 https://로 시작해야 합니다"
                />
              </div>

              <div className="application-form-info-group">
                <label htmlFor="applicationForm">Application Form</label>
                <div className="file-upload-container">
                  <div className="file-upload-item">
                    <button 
                      type="button" 
                      className={`file-select-button ${isFileSelectLoading.applicationForm ? 'loading' : ''}`}
                      onClick={(e) => handleFileSelectClick(e, 'Application Form')}
                      disabled={isFileSelectLoading.applicationForm}
                    >
                      <span className="file-select-icon">+</span>
                      {FILE_SELECT_TEXT}
                    </button>
                    <input
                      type="file"
                      id="applicationForm"
                      name="applicationForm"
                      className="file-input"
                      accept={acceptedFileTypes}
                      onChange={handleFileChange}
                      ref={applicationFormInputRef}
                    />
                  </div>
                  
                  {selectedFiles.applicationForm && (
                    <div className="selected-file-name">
                      <span 
                        className="file-name-text" 
                        onClick={(e) => handleFileClick(e, 'Application Form')}
                        style={{ cursor: 'pointer' }}
                      >
                        {selectedFiles.applicationForm} ({fileSizes.applicationForm})
                      </span>
                      {!hasApplication && <button 
                        type="button"
                        className="file-delete-button"
                        onClick={() => handleDeleteFileClick('applicationForm')}
                        title="Delete file"
                      >
                        <FaTrashAlt />
                      </button>}
                    </div>
                  )}
                  
                  
                  
                  <div className="application-form-announcement-download-link">
                    <a href={announcementUrl} target="_blank" rel="noopener noreferrer">
                      Announcement Download
                      <MdOutlineFileDownload className="download-icon" size={20} />
                    </a>
                  </div>
                </div>
              </div>

              <div className="application-form-info-group">
                <label htmlFor="irPitchDeck">IR Pitch Deck</label>
                <div className="file-upload-container">
                  <div className="file-upload-item">
                    <button 
                      type="button" 
                      className={`file-select-button ${isFileSelectLoading.irPitchDeck ? 'loading' : ''}`}
                      onClick={(e) => handleFileSelectClick(e, 'IR Pitch Deck')}
                      disabled={isFileSelectLoading.irPitchDeck}
                    >
                      <span className="file-select-icon">+</span>
                      {FILE_SELECT_TEXT}
                    </button>
                    <input
                      type="file"
                      id="irPitchDeck"
                      name="irPitchDeck"
                      className="file-input"
                      accept={acceptedFileTypes}
                      onChange={handleFileChange}
                      ref={irPitchDeckInputRef}
                    />
                  </div>
                  
                  {selectedFiles.irPitchDeck && (
                    <div className="selected-file-name">
                      <span 
                        className="file-name-text" 
                        onClick={(e) => handleFileClick(e, 'IR Pitch Deck')}
                        style={{ cursor: 'pointer' }}
                      >
                        {selectedFiles.irPitchDeck} ({fileSizes.irPitchDeck})
                      </span>
                      {!hasApplication && <button 
                        type="button"
                        className="file-delete-button"
                        onClick={() => handleDeleteFileClick('irPitchDeck')}
                        title="Delete file"
                      >
                        <FaTrashAlt />
                      </button>}
                    </div>
                  )}
                  
                </div>
              </div>

              <div className="application-form-info-group">
                <label htmlFor="additionalDocuments">Additional Documents</label>
                <div className="file-upload-container">
                  <div className="file-upload-item">
                    <button 
                      type="button" 
                      className={`file-select-button ${isFileSelectLoading.additionalDocuments ? 'loading' : ''}`}
                      onClick={(e) => handleFileSelectClick(e, 'Additional Documents')}
                      disabled={isFileSelectLoading.additionalDocuments}
                    >
                      <span className="file-select-icon">+</span>
                      {FILE_SELECT_TEXT}
                    </button>
                    <input
                      type="file"
                      id="additionalDocuments"
                      name="additionalDocuments"
                      className="file-input"
                      accept={acceptedFileTypes}
                      onChange={handleFileChange}
                      ref={additionalDocumentsInputRef}
                    />
                  </div>
                  
                  {selectedFiles.additionalDocuments && (
                    <div className="selected-file-name">
                      <span 
                        className="file-name-text" 
                        onClick={(e) => handleFileClick(e, 'Additional Documents')}
                        style={{ cursor: 'pointer' }}
                      >
                        {selectedFiles.additionalDocuments} ({fileSizes.additionalDocuments})
                      </span>
                      {!hasApplication && <button 
                        type="button"
                        className="file-delete-button"
                        onClick={() => handleDeleteFileClick('additionalDocuments')}
                        title="Delete file"
                      >
                        <FaTrashAlt />
                      </button>}
                    </div>
                  )}
                  
                </div>
              </div>

              <div className="application-form-info-group">
                <label htmlFor="informationChannel">Source of Information</label>
                <div className="survey-options">
                  <p className="survey-question">What route did you learn about K-Startup Grand Challenge?</p>
                  <div className="radio-group">
                    {INFORMATION_CHANNELS.map((channel) => (
                      <Form.Check
                        key={channel.id}
                        type="radio"
                        id={channel.id}
                        name="informationChannel"
                        value={channel.value}
                        label={channel.label}
                        checked={formData.informationChannel === channel.value}
                        onChange={handleChange}
                        className="mb-2"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {mode === 'create' && (
              <div className="privacy-agreement-section">
                <h3>Consent to Collection and Use of Personal Information</h3>
                <div className="privacy-notice">
                  <p>With regard to "2025 K-Startup Grand Challenge" the Korea Institute of Startup and Entrepreneurship Development would like to kindly ask for your consent to collection and use of your personal information, and to their provision to third parties, pursuant to Article 15.1.1, Article 17.1.1, Article 23.1, and Article 24.1.1 of Personal Information Protection Act.</p>
                </div>

                <div className="privacy-details">
                  <div className="privacy-item">
                    <h4>■ Purpose</h4>
                    <p>Evaluation for selection of the applicants, operation and management of 2025 K-Startup Grand Challenge</p>
                  </div>

                  <div className="privacy-item">
                    <h4>■ Items</h4>
                    <p>Mandatory Information : Personal identification information (i.e. Name, Nationality, Date of birth, E-mail, Contact number)</p>
                  </div>

                  <div className="privacy-item">
                    <h4>■ Period of Retention/Use</h4>
                    <ul>
                      <li>Personal information above shall be collected and used from the date of the consent and be deleted without delay upon the achievement of the purpose of retention, or upon request from the provider.</li>
                      <li>Provided, personal information above shall be retained and utilized only for the record for future programs. The information shall be retained for the next two years from the date of the evaluation for selection.</li>
                    </ul>
                  </div>

                  <div className="privacy-item">
                    <h4>■ Recipients (Third Parties)</h4>
                    <p>Ministry of SMEs and Startups, Korea Institute of Startup and Entrepreneurship Development, Center for Creative Economy and Innovation.</p>
                  </div>

                  <div className="privacy-item">
                    <h4>■ Rights to Refuse to Agree, and Disadvantage upon the Refusal</h4>
                    <p>Since the consent to the collection/use of mandatory information from the personal information is essential for proceeding with the program, next processes can be continued only when you agree to the above conditions. If you do not agree, you are not able to apply to this program.</p>
                  </div>
                </div>

                <div className="agreement-buttons">
                  <button
                    type="button"
                    className={`agreement-button ${!formData.privacyAgreement ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, privacyAgreement: false }))}
                  >
                    Do not consent
                  </button>
                  <button
                    type="button"
                    className={`agreement-button ${formData.privacyAgreement ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, privacyAgreement: true }))}
                  >
                    Consent
                  </button>
                </div>
              </div>
            )}

            <div className="form-actions">
              <div className="action-buttons">
                <button type="button" onClick={handleCancelClick} className="cancel-button">
                  Cancel
                </button>
                {(mode === 'create' || (hasDraft && mode === 'edit')) &&
                  <button type="button" onClick={handleDraftSave} className="temp-save-button">
                    Save as Draft
                  </button>}
                <button type="submit" className="save-button">
                  Submit
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      
      {/* 모든 모달 컴포넌트들 */}
      {showConsentModal && <ConsentRequiredModal onClose={handleConsentModalClose} />}
      {showOverwriteModal && <OverwriteConfirmModal onConfirm={handleOverwriteConfirm} onCancel={handleOverwriteCancel} fileName={currentFileType === 'applicationForm' ? 'Application Form' : 
                                                                  fileType === 'IR Pitch Deck' ? 'IR/Pitch Deck' :
                                                                  'Additional Documents'} />}
      {showValidationToast && <ValidationToastModal message={validationMessage} isOpen={showValidationToast} onClose={handleValidationToastClose} targetElement={invalidElement} />}
      {showUploadProgressModal && <UploadProgressModal isOpen={showUploadProgressModal} totalProgress={totalProgress} filesProgress={filesProgress} />}
      {showZeroSizeModal && <ZeroSizeFileModal />}
      {showDeleteConfirmModal && <DeleteConfirmModal />}
      {showParticipantInfoModal && <ParticipantInfoModal />}
      {showSaveConfirmModal && (
        <SaveConfirmModal
          isOpen={true}
          onConfirm={modalActionType === 'navigate' ? handleNavigationConfirm : () => setShowSaveConfirmModal(false)}
          onCancel={handleNavigationCancel}
          hasApplication={hasApplication}
          hasDraft={hasDraft}
          actionType={modalActionType}
          isConfirmation={modalActionType !== 'navigate'}
        />
      )}
    </div>
  );
};

ApplicationForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']),
  initialData: PropTypes.shape({
    teamName: PropTypes.string,
    entityStatus: PropTypes.string,
    establishedDate: PropTypes.string,
    location: PropTypes.string,
    ceo: PropTypes.string,
    gender: PropTypes.string,
    participant: PropTypes.string,
    participantTitle: PropTypes.string,
    participantNationality: PropTypes.string,
    industry: PropTypes.string,
    businessItem: PropTypes.string,
    website: PropTypes.string,
    socialMedia: PropTypes.string,
    promotionalVideo: PropTypes.string,
    informationChannel: PropTypes.string,
    privacyAgreement: PropTypes.bool
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  userEmail: PropTypes.string,
  onLogout: PropTypes.func.isRequired
};

export default ApplicationForm; 